import {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import { createPortal } from 'react-dom';
import { useGoogleMap } from '@react-google-maps/api';

export type GoogleAdvancedMarkerInstance =
  google.maps.marker.AdvancedMarkerElement | null;

interface GoogleAdvancedMarkerProps {
  position: google.maps.LatLngLiteral;
  title?: string;
  zIndex?: number;
  className?: string;
  children?: ReactNode;
  onClick?: (event: any) => void;
}

const AdvancedMarker = forwardRef<
  google.maps.marker.AdvancedMarkerElement | null,
  GoogleAdvancedMarkerProps
>((props, ref) => {
  const { position, title, zIndex, children, onClick } = props;

  const map = useGoogleMap();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const contentElement = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    return el;
  }, []);

  useImperativeHandle(ref as any, () => markerRef.current);

  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      title,
      zIndex
    });

    if (children && contentElement) {
      (marker as any).content = contentElement;
    }

    markerRef.current = marker;

    if (onClick) {
      listenersRef.current.push(
        marker.addListener('gmp-click', (event: any) => {
          onClick(event);
        })
      );
    }

    return () => {
      listenersRef.current.forEach((listener) => listener.remove());
      listenersRef.current = [];

      if (markerRef.current) {
        markerRef.current.map = null;
      }

      markerRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.position = position;
    }
  }, [position]);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.title = title || '';
    }
  }, [title]);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  return (
    <>
      {children && contentElement
        ? createPortal(children, contentElement)
        : null}
    </>
  );
});

AdvancedMarker.displayName = 'GoogleAdvancedMarker';

export default AdvancedMarker;
